// The start page: the legacy Dashboard.
//
// On a desktop: "Meine Favoriten" and "Geplante Ansagen" over a pane of frosted
// glass on the background illustration — the favourites and "Neue Ansage" on
// the left, the planned announcements on the right. On a phone: the same two
// headings under the illustrated bar, the list, and "Neue Ansage" fixed at the
// bottom. Measures, colours and words are Dashboard.js's.

import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  Fade,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { lighten } from "polished";
import { useNavigate } from "react-router-dom";
import type { Card, FavouriteCard } from "~/api";
import { useAnnouncements, useBootstrap, useRefresh } from "~/bootstrap";
import { useT } from "~/i18n";
import { DefaultPrimary, softText } from "~/theme";
import { useIntro, startIntro } from "~/intro";
import { addMinutes, durationText, startOfDay } from "~/components/clock/dates";
import { AnnouncementIcon, isOffice, modeFor, titleKey } from "~/legacy/helpers";
import { DashboardDrawer, type DrawerRequest } from "./drawer";
import { DesktopListItem, MobileListItem, type Sound } from "./listitem";
import { SoundDialog } from "./sound";

/** useWidth follows an element's width, as the legacy resize listeners did. */
function useWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth || fallback);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, width] as const;
}

/** The legacy desktop background: the same SVG, the same crop. */
function backgroundFor(primary: string, dark: boolean) {
  if (dark) return "/images/desktop-background-dark.svg";
  return primary === DefaultPrimary ? "/images/desktop-background-default.svg" : "/images/desktop-background-light.svg";
}

export function HomePage() {
  const theme = useTheme();
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useT();
  const b = useBootstrap();
  const { data: cards } = useAnnouncements();
  const refresh = useRefresh();
  const navigate = useNavigate();

  const intro = useIntro();
  const [splash] = useState(() => startIntro());
  const hidden = intro === "running";

  const [request, setRequest] = useState<DrawerRequest | null>(null);
  const [sound, setSound] = useState<Sound | null>(null);
  const [error, setError] = useState("");

  const [paperRef, paperWidth] = useWidth<HTMLDivElement>(window.innerWidth * 0.56);
  const [entriesRef, entriesWidth] = useWidth<HTMLDivElement>(window.innerWidth * 0.9);

  const [height, setHeight] = useState(() =>
    document.body.clientHeight < 700 ? document.body.clientHeight * 0.03 : document.body.clientHeight * 0.1,
  );
  useEffect(() => {
    const onResize = () =>
      setHeight(document.body.clientHeight < 700 ? document.body.clientHeight * 0.05 : document.body.clientHeight * 0.1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // The body carries the ground: plain on a phone and during the splash, the
  // tinted illustration on a desktop.
  useEffect(() => {
    document.title = t("vmm-dashboard");
    const s = document.body.style;
    const plain = belowsm || hidden;
    s.backgroundColor = plain ? theme.palette.background.default : theme.palette.background.secondary;
    s.backgroundImage = plain ? "none" : `url(${backgroundFor(theme.palette.primary.main, theme.palette.mode === "dark")})`;
    s.backgroundSize = "cover";
    s.backgroundPosition = "-260px 380px";
    s.backgroundRepeat = "no-repeat";
    return () => {
      s.backgroundColor = "";
      s.backgroundImage = "";
    };
  }, [belowsm, hidden, theme, t]);

  const list = cards ?? [];
  const descriptorOf = (c: Card) => b.messages.find((m) => m.companyMessageId === c.companyMessageId);

  const openNew = () => {
    // The phone went through the wizard, unless a company had named its
    // announcement types itself (a subtitle), in which case the drawer's own
    // menu said enough.
    const hasSubtitle = b.messages.some((m) => (m.subtitle ?? "").trim() !== "");
    if (belowsm && !hasSubtitle) navigate("/neu?type=announcement");
    else setRequest({ kind: "new" });
  };

  const openFavourite = (slot: number, f: FavouriteCard | undefined) => {
    const hasSubtitle = b.messages.some((m) => (m.subtitle ?? "").trim() !== "");
    if (belowsm && (!f || f.empty) && !hasSubtitle) navigate(`/neu?type=favourite&slot=${slot}`);
    else setRequest({ kind: "favourite", slot, favourite: f && !f.empty ? f : undefined });
  };

  const newEntryButton = (sx: object) => (
    <Button
      variant="contained"
      onClick={openNew}
      startIcon={
        <Avatar sx={{ bgcolor: "primary.contrastText", height: 43, width: 43, ml: 0.15 }}>
          <AddIcon sx={{ color: "primary.main", fontSize: 32 }} />
        </Avatar>
      }
      sx={{
        bgcolor: "primary.main",
        borderRadius: 6,
        textTransform: "none",
        justifyContent: "flex-start",
        pl: 1,
        height: 52,
        "&:hover": { bgcolor: lighten(0.05, theme.palette.primary.main) },
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Typography sx={{ color: "primary.contrastText", fontSize: 22, fontWeight: "bold" }}>{t("new-entry")}</Typography>
      </Box>
    </Button>
  );

  const notes = b.me.recordingNoteKey ? (
    <Alert severity="info" sx={{ mx: 2, mb: 1, borderRadius: 3 }}>
      {t(b.me.recordingNoteKey)}
    </Alert>
  ) : null;

  const desktop = (
    <Grid container justifyContent="center" sx={{ visibility: hidden ? "hidden" : "visible" }}>
      <Grid item xs={11} sm={11} md={9} lg={7}>
        <Grid container sx={{ mt: 5, mb: 1 }}>
          <Grid item sm={7} md={6}>
            <Typography
              noWrap
              sx={{ color: "text.primary", fontWeight: 700, lineHeight: "normal", fontSize: "40px", letterSpacing: "-1px", ml: 2 }}
            >
              {t("voicemails")}
            </Typography>
          </Grid>
          <Grid item sm={5} md={6}>
            <Typography
              noWrap
              sx={{ color: "text.primary", fontWeight: 700, lineHeight: "normal", fontSize: "20px", letterSpacing: "-0.5px", mt: 2.7 }}
            >
              {t("saved-voicemails")}
            </Typography>
          </Grid>
        </Grid>
        {notes}
        <Paper
          ref={paperRef}
          sx={{
            borderRadius: "36px",
            background: theme.palette.mode === "dark" ? "rgba(12, 12, 12, 0.35)" : "rgba(255, 255, 255, 0.35)",
            backdropFilter: "blur(5px)",
            width: "100%",
            height: 446,
            overflow: "hidden",
          }}
        >
          <Grid container sx={{ mt: 1, mb: 1 }}>
            <Grid item xs={6} sx={{ pl: 2, pr: 3 }}>
              <Grid container direction="column">
                <QuickStart onOpen={openFavourite} />
                {newEntryButton({ mr: 2, mt: 10 })}
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <TableContainer sx={{ p: 2, pt: 3, pl: 0, maxHeight: 430, "::-webkit-scrollbar": { display: "none" } }}>
                <Table>
                  <TableBody>
                    {list.length > 0 ? (
                      list.map((c) => (
                        <DesktopListItem
                          key={c.id}
                          card={c}
                          descriptor={descriptorOf(c)}
                          paperWidth={paperWidth}
                          onEdit={(card) => setRequest({ kind: "edit", card })}
                          onDone={refresh}
                          onError={setError}
                          onSound={setSound}
                        />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          sx={{ display: "flex", justifyContent: "center", width: "100%", height: 219, borderBottom: 0, alignItems: "center", mt: 1 }}
                        >
                          <Typography color="text.secondary">{t("no-voicemails-planned")}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Fragment>
      <DashboardDrawer request={request} onClose={() => setRequest(null)} />
      {belowsm ? (
        <Box sx={{ bgcolor: "transparent", visibility: hidden ? "hidden" : "visible" }}>
          <Typography
            sx={{
              color: "text.primary",
              fontWeight: 700,
              lineHeight: "34px",
              fontSize: 30,
              mt: -3,
              ml: 2,
              position: "relative",
              zIndex: theme.zIndex.appBar + 101,
            }}
          >
            {t("voicemails")}
          </Typography>
          <Box sx={{ mt: -0.5, ml: 2, mr: 1 }}>
            <QuickStart onOpen={openFavourite} />
          </Box>
          <Typography sx={{ color: "text.primary", fontWeight: "bold", fontSize: 20, ml: 2, mt: 2 }}>{t("saved-voicemails")}</Typography>
        </Box>
      ) : null}
      {belowsm ? (
        <Box sx={{ mx: 2, mt: 2.5, mb: list.length > 0 ? 9 : 0, visibility: hidden ? "hidden" : "visible" }}>
          {notes}
          <TableContainer ref={entriesRef} id="entries-container" sx={{ border: 0 }}>
            <Table>
              <TableBody>
                {list.length > 0 ? (
                  list.map((c) => (
                    <MobileListItem
                      key={c.id}
                      card={c}
                      descriptor={descriptorOf(c)}
                      onEdit={(card) => setRequest({ kind: "edit", card })}
                      onDone={refresh}
                      onError={setError}
                      onSound={setSound}
                    />
                  ))
                ) : (
                  <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                      <Box sx={{ height }} />
                      <Typography color="text.secondary">{t("no-voicemails-planned")}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Fade in={!hidden} timeout={{ enter: splash ? 1000 : 0, exit: 0 }}>
          <div>{desktop}</div>
        </Fade>
      )}
      <Box sx={{ display: belowsm ? "inline" : "none", visibility: hidden ? "hidden" : "visible", mt: 2, ml: 2, mr: 3 }}>
        {newEntryButton({ position: "fixed", bottom: theme.spacing(3), width: entriesWidth })}
      </Box>
      <SoundDialog
        url={sound?.url ?? null}
        message={sound?.message ?? ""}
        startsAt={sound?.startsAt}
        endsAt={sound?.endsAt}
        noEnd={sound?.noEnd ?? false}
        onClose={() => setSound(null)}
      />
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError("")}>
        <Alert severity="error" onClose={() => setError("")} sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
      <SplashScreen open={hidden} />
    </Fragment>
  );
}

/** The legacy SplashScreen: a full-screen dialog over everything for 2.6 seconds. */
function SplashScreen({ open }: { open: boolean }) {
  const theme = useTheme();
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useT();
  const primary = theme.palette.primary.main;
  const logo =
    primary === DefaultPrimary ? "/images/logo-default.png" : theme.palette.mode === "dark" ? "/images/logo-dark.png" : "/images/logo-light.png";
  return (
    <Dialog
      open={open}
      fullScreen
      hideBackdrop
      transitionDuration={{ enter: 2000, exit: 600 }}
      sx={{
        "& .MuiDialog-paper": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: belowsm ? "background.default" : "background.secondary",
          backgroundImage: belowsm ? "none" : `url(${backgroundFor(primary, theme.palette.mode === "dark")})`,
          backgroundSize: "cover",
          backgroundPosition: "-260px 380px",
          backgroundRepeat: "no-repeat",
        },
      }}
    >
      <Box sx={{ height: 90, width: "100%", bgcolor: "background.default", position: "absolute", top: 0 }} />
      <Box sx={{ pl: 7.5, pt: 20 }}>{belowsm ? <img src={logo} alt="vmm-logo" height={250} /> : null}</Box>
      <Grid container justifyContent="center" sx={{ pt: 20 }}>
        <Typography
          sx={{
            color: "text.primary",
            lineHeight: "34px",
            fontSize: 30,
            letterSpacing: "-1px",
            position: belowsm ? "relative" : "absolute",
            bottom: belowsm ? "auto" : 20,
            right: belowsm ? "auto" : 20,
          }}
        >
          {t("powered-by")}
        </Typography>
      </Grid>
    </Dialog>
  );
}

// --- the favourites ----------------------------------------------------------------------

/**
 * The legacy Quickstart: five tiles in two columns, 256px tall — on the left
 * the first favourite over two thirds and the fourth under it, on the right
 * the second, third and fifth in thirds.
 */
function QuickStart({ onOpen }: { onOpen: (slot: number, f: FavouriteCard | undefined) => void }) {
  const theme = useTheme();
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const b = useBootstrap();
  const [tallRef, width] = useWidth<HTMLButtonElement>(belowsm ? window.innerWidth * 0.43 : window.innerWidth * 0.13);

  const at = (n: number) => b.favourites.find((f) => f.slot === n && !f.empty);

  const tile = (slot: number, tall = false) => (
    <Tile
      slot={slot}
      favourite={at(slot)}
      tall={tall}
      width={width}
      buttonRef={tall ? tallRef : undefined}
      onOpen={onOpen}
    />
  );

  return (
    <Box sx={{ display: "inline" }}>
      <Grid container sx={{ pl: 1, display: "inline" }}>
        <Grid container direction="row" columnGap={1}>
          <Grid item xs={5.7}>
            <Grid container direction="column" height={256}>
              <Grid item xs={8} sx={{ height: "67%", pb: 1 }}>
                {tile(0, true)}
              </Grid>
              <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                {tile(3)}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={5.8}>
            <Grid container direction="column" height={256}>
              <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                {tile(1)}
              </Grid>
              <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                {tile(2)}
              </Grid>
              <Grid item xs={4} sx={{ height: "33%", pb: 1 }}>
                {tile(4)}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

function Tile({
  slot,
  favourite,
  tall,
  width,
  buttonRef,
  onOpen,
}: {
  slot: number;
  favourite: FavouriteCard | undefined;
  tall: boolean;
  width: number;
  buttonRef?: React.Ref<HTMLButtonElement>;
  onOpen: (slot: number, f: FavouriteCard | undefined) => void;
}) {
  const theme = useTheme();
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useT();
  const b = useBootstrap();
  const soft = softText(theme);

  const d = favourite ? b.messages.find((m) => m.companyMessageId === favourite.companyMessageId) : undefined;
  const mode = modeFor(d, favourite?.icon);
  const title = favourite ? t(titleKey(mode)) : "";
  const duration = !favourite
    ? ""
    : favourite.openEnded || !favourite.durationMinutes
      ? t("no-end")
      : (() => {
          const start = startOfDay(new Date());
          return durationText({ start, end: addMinutes(start, favourite.durationMinutes) }, mode, t);
        })();
  const representation = !favourite
    ? ""
    : isOffice(d)
      ? t("office-representation")
      : favourite.proxy?.name
        ? favourite.proxy.name
        : t("no-rep");

  return (
    <Button
      ref={buttonRef}
      id={tall ? "favorites-0" : undefined}
      variant="contained"
      color="secondary"
      onClick={() => onOpen(slot, favourite)}
      disableElevation={belowsm}
      disabled={!!favourite && !favourite.available}
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 3,
        textTransform: "none",
        px: 0,
        "&:hover": { bgcolor: lighten(0.02, theme.palette.secondary.main) },
      }}
    >
      {!favourite ? (
        <Grid container justifyContent="center">
          <AddIcon sx={{ fontSize: 32, mr: 2 }} />
          <Typography
            noWrap
            sx={{ fontSize: "16px", fontWeight: "normal", lineHeight: "normal", textOverflow: "ellipsis", mt: 0.7, mr: 2 }}
          >
            {t("new-quickstart")}
          </Typography>
        </Grid>
      ) : tall ? (
        <Grid container direction="column" justifyContent="flex-start" sx={{ width: "100%", height: "100%" }}>
          <Grid item>
            <Grid container direction="row">
              <Grid item>
                <AnnouncementIcon icon={favourite.icon} fontSize={48} />
              </Grid>
              <Grid item>
                <Typography
                  align="left"
                  noWrap
                  sx={{ fontSize: belowsm ? "11px" : "12px", fontWeight: 400, lineHeight: "normal", width: width - 80, mt: 0.5, color: soft }}
                >
                  {title}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Box sx={{ flexGrow: 1 }} />
          <Grid container direction="column" sx={{ overflow: "hidden", pl: 1 }}>
            <Grid item>
              <Typography
                noWrap
                align="left"
                sx={{ fontSize: belowsm ? "14px" : "16px", width: width - 25, fontWeight: 400, lineHeight: "normal", pl: 0.5, color: "secondary.contrastText" }}
              >
                {duration}
              </Typography>
            </Grid>
            <Grid item>
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Typography
                  noWrap
                  align="left"
                  sx={{ fontSize: belowsm ? "12px" : "16px", overflow: "hidden", width: width - 25, fontWeight: 400, lineHeight: "normal", pl: 0.5, mt: 0.2, color: soft }}
                >
                  {representation}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Typography
              noWrap
              align="left"
              sx={{ fontSize: belowsm ? "11px" : "12px", width: width - 15, fontWeight: 400, lineHeight: "normal", pl: 1.5, mb: 0.5, color: soft }}
            >
              {title}
            </Typography>
          </Box>
          <Grid container>
            <Grid item xs={4} sx={{ overflow: "hidden" }}>
              <Grid container sx={{ pl: 1 }}>
                <AnnouncementIcon icon={favourite.icon} />
              </Grid>
            </Grid>
            <Grid item xs={8}>
              <Grid container direction="column" sx={{ overflow: "hidden" }}>
                <Grid item>
                  <Typography
                    noWrap
                    align="left"
                    sx={{ fontSize: belowsm ? "14px" : "16px", width: width - 70, fontWeight: 400, lineHeight: "normal", pl: 0.5, color: "secondary.contrastText" }}
                  >
                    {duration}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    noWrap
                    align="left"
                    sx={{ fontSize: belowsm ? "12px" : "16px", overflow: "hidden", width: width - 70, fontWeight: 400, lineHeight: "normal", pl: 0.5, mt: 0.2, color: soft }}
                  >
                    {representation}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}
    </Button>
  );
}
